import { useState, useEffect } from "react";
import axios from "axios";
import { LibraryNavigation } from "../components/navigation/LibraryNavigation";
import { Link } from "react-router-dom";
import { API } from "../App";
import { toast } from "sonner";
import { useAuth } from "../App";
import { ArrowLeft, Download, Radio, Trophy, ShoppingBag, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";

export default function MyOrders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/my/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setOrders(res.data?.orders || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load orders");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleDownload = async (productId) => {
    try {
      const res = await axios.get(`${API}/products/${productId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = res.data?.download_url;
      if (!url) { toast.error("No file available"); return; }
      // Build absolute URL if it's a relative /api path
      const absolute = url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;
      window.open(absolute, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050810] text-white flex items-center justify-center">Loading your orders…</div>;
  }

  return (
    <div className="min-h-screen bg-[#050810] text-white pb-20" data-testid="my-orders-page">
      <header className="sticky top-0 z-30 glass-strong border-b border-[#1A2030]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="text-[#8A92A0] hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <ShoppingBag className="w-6 h-6 text-[#EC008C]" />
          <h1 className="font-['Unbounded'] text-xl font-bold">My Purchases</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <LibraryNavigation />
        {orders.length === 0 ? (
          <div className="card p-10 text-center" data-testid="my-orders-empty">
            <ShoppingBag className="w-10 h-10 text-[#EC008C] mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">No purchases yet</p>
            <p className="text-[#8A92A0] text-sm mb-4">Browse the storefront to support your favourite artists.</p>
            <Link to="/feed" className="text-[#EC008C] hover:underline text-sm">Discover artists →</Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="my-orders-list">
            {orders.map(o => (
              <div key={o.order_id} className="card p-5 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between" data-testid={`my-order-${o.order_id}`}>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                      o.status === "shipped" ? "bg-emerald-500/15 text-emerald-400" :
                      o.status === "fulfilled" ? "bg-blue-500/15 text-blue-400" :
                      "bg-[#EC008C]/15 text-[#EC008C]"
                    }`}>{(o.status || "").replace("_", " ")}</span>
                    <span className="text-[10px] bg-[#1A2030] text-[#8A92A0] px-2 py-0.5 rounded-full">
                      {o.product_category || "product"}
                    </span>
                  </div>
                  <h3 className="font-semibold text-white">{o.product_name}</h3>
                  <p className="text-[#8A92A0] text-xs mt-0.5">
                    ${(o.amount || 0).toFixed(2)} · {new Date(o.created_at).toLocaleString()}
                  </p>
                  {o.tracking_number && (
                    <p className="text-emerald-400 text-xs mt-1">
                      Tracking: {o.tracking_number} {o.carrier && `(${o.carrier})`}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {o.has_digital_file && (
                    <Button
                      onClick={() => handleDownload(o.product_id)}
                      className="bg-[#EC008C] text-black hover:bg-[#FF3399]"
                      data-testid={`download-btn-${o.order_id}`}
                    >
                      <Download className="w-4 h-4 mr-1.5" /> Download
                    </Button>
                  )}
                  {o.livestream_url && (
                    <a
                      href={o.livestream_url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 rounded-md bg-[#1A2030] text-white text-sm hover:bg-[#1A2030]/70 inline-flex items-center gap-1.5"
                      data-testid={`join-livestream-${o.order_id}`}
                    >
                      <Radio className="w-4 h-4" /> Join livestream
                    </a>
                  )}
                  {o.product_category === "challenge" && (
                    <Link
                      to={`/products/${o.product_id}?action=submit-entry`}
                      className="px-4 py-2 rounded-md bg-[#1A2030] text-white text-sm hover:bg-[#1A2030]/70 inline-flex items-center gap-1.5"
                      data-testid={`submit-entry-${o.order_id}`}
                    >
                      <Trophy className="w-4 h-4" /> Submit entry
                    </Link>
                  )}
                  <Link
                    to={o.item_type === "collectible" ? "/my-collectibles" : `/products/${o.product_id}`}
                    data-testid={`order-details-${o.order_id}`}
                    className="text-xs text-[#8A92A0] hover:text-white inline-flex items-center gap-1 px-2 py-2"
                  >
                    <ExternalLink className="w-3 h-3" /> {o.item_type === "collectible" ? "My collectible" : "Details"}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
